import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationItem } from '@shared/models/navigation-item';
import { Observable } from 'rxjs';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';

@Injectable({
  providedIn: 'root',
})
export class SidebarItemListService {
  selectedCountry: WritableSignal<
    InvestmentJurisdictionShort | null | undefined
  > = signal(null);
  selectedCountry$: Observable<InvestmentJurisdictionShort | null | undefined> =
    toObservable(this.selectedCountry);

  hasCountryChanged: WritableSignal<boolean> = signal(false);
  hasCountryChanged$: Observable<boolean> = toObservable(
    this.hasCountryChanged
  );

  basicNavListSecondPart: Signal<NavigationItem[]> = signal([
    {
      name: 'Country information',
      prefixIcon: 'globe',
      suffixIcon: 'down-chevron',
      prefixCollapsedIcon: 'globe',
      hasFilter: true,
      children: [
        {
          name: 'General information',
          suffixIcon: 'down-chevron',
          disableIf: (): boolean => {
            return this.selectedCountry()?.investmentJurisdictionId
              ? false
              : true;
          },
          children: [
            {
              name: 'Blacklist information',
              routerLink: `/country/{countryId}/general-information/1`,
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
            {
              name: 'Look through rules',
              routerLink: '/country/{countryId}/general-information/2',
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
            {
              name: 'Sovereign wealth funds / domestic fund regime exemptions',
              routerLink: '/country/{countryId}/general-information/3',
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
            {
              name: 'Other taxes, general considerations',
              routerLink: '/country/{countryId}/general-information/4',
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
            {
              name: 'Procedures',
              routerLink: '/country/{countryId}/general-information/5',
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
            {
              name: 'Tax treaties',
              routerLink: '/country/{countryId}/general-information/6',
              disableIf: (): boolean => {
                return this.selectedCountry()?.investmentJurisdictionId
                  ? false
                  : true;
              },
            },
          ],
        },
        {
          name: 'Tax rate summaries',
          routerLink: '/country/{countryId}/tax-rates-summaries',
          disableIf: (): boolean => {
            return this.selectedCountry()?.investmentJurisdictionId
              ? false
              : true;
          },
        },
        {
          name: 'Tax news',
          routerLink: '/country/{countryId}/tax-news',
          disableIf: (): boolean => {
            return this.selectedCountry()?.investmentJurisdictionId
              ? false
              : true;
          },
        },
      ],
    },
    {
      name: 'Manage entities',
      routerLink: '/entities',
      prefixIcon: 'org-chart',
      prefixCollapsedIcon: 'org-chart',
    },
    {
      name: 'Audit history',
      routerLink: '/audit-history',
      prefixIcon: 'refresh',
      prefixCollapsedIcon: 'refresh',
    },
  ]);

  basicNavListFirstPart: Signal<NavigationItem[]> = signal([
    {
      name: 'Dashboard',
      routerLink: '/dashboard',
      prefixIcon: 'home',
      prefixCollapsedIcon: 'home',
    },
    {
      name: 'Portfolio',
      routerLink: '/portfolio',
      prefixIcon: 'capital',
      prefixCollapsedIcon: 'capital',
    },
  ]);

  basicNavList: Signal<NavigationItem[]> = signal([
    ...this.basicNavListFirstPart(),
    ...this.basicNavListSecondPart(),
  ]);

  countryAdminNavList: Signal<NavigationItem[]> = signal([
    this.basicNavListSecondPart()[0],
  ]);

  adminNavList: Signal<NavigationItem[]> = signal([
    ...this.basicNavListFirstPart(),
    {
      name: 'Tax result',
      routerLink: '/tax-results',
      prefixIcon: 'accept',
      prefixCollapsedIcon: 'accept',
    },
    ...this.basicNavListSecondPart(),
    {
      name: 'Admin',
      routerLink: '/admin',
      prefixIcon: 'account-box',
      prefixCollapsedIcon: 'account-box',
    },
  ]);

  getNavList(
    isAdmin: boolean,
    isCountryAdmin: boolean,
    country: InvestmentJurisdictionShort | null | undefined
  ): NavigationItem[] {
    this.selectedCountry.set(country);
    if (isAdmin) {
      return this.adminNavList();
    }
    if (isCountryAdmin) {
      return this.countryAdminNavList();
    }
    return this.basicNavList();
  }
}
